// 114 — Bitácora UCI Neonatal (Fase 4, Tanda 6a, ver PLAN.md). Bloque B
// (constantes vitales de ingreso) y el alcance de Bloque C/D/E/G descrito
// en `bloques114Datos.ts` (datos de bitácora, diagnóstico, gráfico de
// temperatura, gráfico de PA/pulso/FR, medicación, ingesta, balance
// hídrico, gasometría).
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import {
  CAMPOS_BALANCE_HIDRICO_114,
  CAMPOS_CONSTANTES_INGRESO_114,
  COLUMNAS_INTERCONSULTAS_114,
  COLUMNAS_PROCEDIMIENTOS_114,
  COLUMNAS_SOLICITUD_EXAMENES_114,
  FILAS_GASOMETRIA_114,
  FILAS_INGESTA_EGRESOS_114,
  FILAS_INGESTA_INGRESOS_114,
  FILAS_INTERCONSULTAS_114,
  FILAS_PROCEDIMIENTOS_ENFERMERIA_114,
  FILAS_PROCEDIMIENTOS_OTROS_114,
  COLUMNAS_PROCEDIMIENTOS_ENFERMERIA_HORAS_114,
  COLUMNAS_PROCEDIMIENTOS_OTROS_114,
  TOPE_OTROS_HORARIOS_114,
  tablasBalance114,
  type GruposNombrables114,
  FILAS_PROCEDIMIENTOS_114,
  FILAS_SOLICITUD_EXAMENES_114,
  FILAS_VENTILACION_114,
  ITEMS_PROCEDIMIENTOS_ENFERMERIA_114,
  OPCIONES_CONDICION_EDAD_114,
  OPCIONES_UNIDAD_NEONATAL_114,
  SERIES_GRAFICO_PA_PULSO_FR_114,
  SERIE_GRAFICO_TEMPERATURA_114,
  balanceHidricoEfectivo114,
  CAMPOS_BALANCE_CALCULABLES_114,
  type CondicionEdad114,
  type EstadoBalanceHidrico114,
  type EstadoConstantesIngreso114,
  type EstadoDatosBitacora114,
  type EstadoInformeEnfermeria114,
  type UnidadNeonatal114,
} from './bloques114Datos';
import { CampoBalanceHidrico } from './uci/CampoBalanceHidrico';
import { CamposClinicosUci } from './uci/CamposClinicosUci';
import { FilasNombrablesHorarias } from './uci/FilasNombrablesHorarias';
import { TablaHoraria24h, type EstadoTablaHoraria24h } from './uci/TablaHoraria24h';
import { GraficoEnfermeria, type PuntosGraficoEnfermeria } from './uci/GraficoEnfermeria';
import { ChecklistTurno, type EstadoChecklistTurno } from './uci/ChecklistTurno';
import { TablaConColumnas, type EstadoTablaConColumnas } from './uci/TablaConColumnas';

function CampoTexto({ id, label, value, onChange, tipo }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void; readonly tipo?: string }): JSX.Element {
  if (tipo) {
    return (
      <div className="flex flex-col gap-1">
        <Label htmlFor={id}>{label}</Label>
        <Input id={id} type={tipo} value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  return <CampoDictado id={id} etiqueta={label} valor={value} onChange={onChange} />;
}

function BloqueConstantesIngreso({ estado, onChange }: { readonly estado: EstadoConstantesIngreso114; readonly onChange: (v: EstadoConstantesIngreso114) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.constantesIngresoTitulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
        {CAMPOS_CONSTANTES_INGRESO_114.map((c) => (
          <CampoTexto key={c} id={`f114-b-${c}`} label={t(`formularios.f114.constantesIngreso.${c}`)} value={estado[c]} onChange={(v) => onChange({ ...estado, [c]: v })} />
        ))}
      </div>
    </div>
  );
}

function BloqueDatosBitacora({ estado, onChange }: { readonly estado: EstadoDatosBitacora114; readonly onChange: (v: EstadoDatosBitacora114) => void }): JSX.Element {
  const { t } = useTranslation();
  function actualizar(cambios: Partial<EstadoDatosBitacora114>): void {
    onChange({ ...estado, ...cambios });
  }
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.datosBitacora.titulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoTexto id="f114-fecha-ingreso" tipo="date" label={t('formularios.f114.datosBitacora.fechaIngresoUci')} value={estado.fechaIngresoUci} onChange={(v) => actualizar({ fechaIngresoUci: v })} />
        <CampoTexto id="f114-fecha-actual" tipo="date" label={t('formularios.f114.datosBitacora.fechaActual')} value={estado.fechaActual} onChange={(v) => actualizar({ fechaActual: v })} />
        <CamposClinicosUci prefijoId="f114" estado={estado} onChange={(v) => actualizar(v)} />
        <CampoTexto id="f114-peso-diario" label={t('formularios.f114.datosBitacora.pesoDiario')} value={estado.pesoDiario} onChange={(v) => actualizar({ pesoDiario: v })} />
        <div className="flex flex-col gap-1">
          <Label htmlFor="f114-condicion-edad">{t('formularios.f114.datosBitacora.condicionEdad')}</Label>
          <Select value={estado.condicionEdad || undefined} onValueChange={(v) => actualizar({ condicionEdad: v as CondicionEdad114 })}>
            <SelectTrigger id="f114-condicion-edad">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPCIONES_CONDICION_EDAD_114.map((o) => (
                <SelectItem key={o} value={o}>
                  {t(`formularios.f114.condicionEdadOpciones.${o}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="f114-unidad-neonatal">{t('formularios.f114.datosBitacora.unidadNeonatal')}</Label>
          <Select value={estado.unidadNeonatal || undefined} onValueChange={(v) => actualizar({ unidadNeonatal: v as UnidadNeonatal114 })}>
            <SelectTrigger id="f114-unidad-neonatal">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPCIONES_UNIDAD_NEONATAL_114.map((o) => (
                <SelectItem key={o} value={o}>
                  {t(`formularios.f114.unidadNeonatalOpciones.${o}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function BloqueBalanceHidrico({ estado, ingresos, egresos, onChange }: { readonly estado: EstadoBalanceHidrico114; readonly ingresos: EstadoTablaHoraria24h; readonly egresos: EstadoTablaHoraria24h; readonly onChange: (v: EstadoBalanceHidrico114) => void }): JSX.Element {
  const { t } = useTranslation();
  const efectivo = balanceHidricoEfectivo114(estado, ingresos, egresos);
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.balanceHidricoTitulo')}</h4>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {CAMPOS_BALANCE_HIDRICO_114.map((c) => (
          <CampoBalanceHidrico
            key={c}
            id={`f114-balance-${c}`}
            label={t(`formularios.f114.balanceHidrico.${c}`)}
            manual={estado[c]}
            efectivo={efectivo[c]}
            calculable={CAMPOS_BALANCE_CALCULABLES_114.includes(c)}
            onChange={(v) => onChange({ ...estado, [c]: v })}
          />
        ))}
      </div>
    </div>
  );
}

function BloqueGraficoUnaSerie({
  titulo,
  puntos,
  onChange,
}: {
  readonly titulo: string;
  readonly puntos: PuntosGraficoEnfermeria;
  readonly onChange: (v: PuntosGraficoEnfermeria) => void;
}): JSX.Element {
  const serie = SERIE_GRAFICO_TEMPERATURA_114[0]!;
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-medium text-hce-ink">{titulo}</h4>
      <GraficoEnfermeria series={SERIE_GRAFICO_TEMPERATURA_114} puntos={puntos} onChange={onChange} serieActiva={serie.clave} onChangeSerieActiva={() => {}} />
    </div>
  );
}

function BloqueGraficoPaPulsoFr({ puntos, onChange }: { readonly puntos: PuntosGraficoEnfermeria; readonly onChange: (v: PuntosGraficoEnfermeria) => void }): JSX.Element {
  const { t } = useTranslation();
  const [serieActiva, setSerieActiva] = useState(SERIES_GRAFICO_PA_PULSO_FR_114[0]!.clave);
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.graficoPaPulsoFrTitulo')}</h4>
      <GraficoEnfermeria series={SERIES_GRAFICO_PA_PULSO_FR_114} puntos={puntos} onChange={onChange} serieActiva={serieActiva} onChangeSerieActiva={setSerieActiva} />
    </div>
  );
}

function BloqueInformeEnfermeria114({ estado, onChange }: { readonly estado: EstadoInformeEnfermeria114; readonly onChange: (v: EstadoInformeEnfermeria114) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.informeEnfermeriaTitulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(['manana', 'tarde', 'noche'] as const).map((turno) => (
          <AreaDictado
            key={turno}
            id={`f114-informe-${turno}`}
            etiqueta={t(`formularios.f114.informeEnfermeria.${turno}`)}
            valor={estado[turno]}
            onChange={(v) => onChange({ ...estado, [turno]: v })}
            filas={8}
          />
        ))}
      </div>
      <p className="text-xs text-hce-muted">{t('formularios.uci.informe.ayuda')}</p>
    </div>
  );
}

export interface Bloques114Props {
  readonly constantesIngreso: EstadoConstantesIngreso114;
  readonly onChangeConstantesIngreso: (v: EstadoConstantesIngreso114) => void;
  readonly datosBitacora: EstadoDatosBitacora114;
  readonly onChangeDatosBitacora: (v: EstadoDatosBitacora114) => void;
  readonly diagnostico: DiagnosticoCie10Valor;
  readonly onChangeDiagnostico: (v: DiagnosticoCie10Valor) => void;
  readonly graficoTemperatura: PuntosGraficoEnfermeria;
  readonly onChangeGraficoTemperatura: (v: PuntosGraficoEnfermeria) => void;
  readonly graficoPaPulsoFr: PuntosGraficoEnfermeria;
  readonly onChangeGraficoPaPulsoFr: (v: PuntosGraficoEnfermeria) => void;
  readonly grupos: GruposNombrables114;
  readonly onChangeGrupos: (v: GruposNombrables114) => void;
  readonly ingestaIngresos: EstadoTablaHoraria24h;
  readonly onChangeIngestaIngresos: (v: EstadoTablaHoraria24h) => void;
  readonly ingestaEgresos: EstadoTablaHoraria24h;
  readonly onChangeIngestaEgresos: (v: EstadoTablaHoraria24h) => void;
  readonly balanceHidrico: EstadoBalanceHidrico114;
  readonly onChangeBalanceHidrico: (v: EstadoBalanceHidrico114) => void;
  readonly gasometria: EstadoTablaHoraria24h;
  readonly onChangeGasometria: (v: EstadoTablaHoraria24h) => void;
  readonly ventilacion: EstadoTablaHoraria24h;
  readonly onChangeVentilacion: (v: EstadoTablaHoraria24h) => void;
  readonly procedimientos: EstadoTablaConColumnas;
  readonly onChangeProcedimientos: (v: EstadoTablaConColumnas) => void;
  readonly procedimientosEnfermeria: EstadoChecklistTurno;
  readonly onChangeProcedimientosEnfermeria: (v: EstadoChecklistTurno) => void;
  readonly procedimientosEnfermeriaHoras: EstadoTablaConColumnas;
  readonly onChangeProcedimientosEnfermeriaHoras: (v: EstadoTablaConColumnas) => void;
  readonly procedimientosOtros: EstadoTablaConColumnas;
  readonly onChangeProcedimientosOtros: (v: EstadoTablaConColumnas) => void;
  readonly solicitudExamenes: EstadoTablaConColumnas;
  readonly onChangeSolicitudExamenes: (v: EstadoTablaConColumnas) => void;
  readonly interconsultas: EstadoTablaConColumnas;
  readonly onChangeInterconsultas: (v: EstadoTablaConColumnas) => void;
  readonly informeEnfermeria: EstadoInformeEnfermeria114;
  readonly onChangeInformeEnfermeria: (v: EstadoInformeEnfermeria114) => void;
}

export function Bloques114({
  constantesIngreso,
  onChangeConstantesIngreso,
  datosBitacora,
  onChangeDatosBitacora,
  diagnostico,
  onChangeDiagnostico,
  graficoTemperatura,
  onChangeGraficoTemperatura,
  graficoPaPulsoFr,
  onChangeGraficoPaPulsoFr,
  grupos,
  onChangeGrupos,
  ingestaIngresos,
  onChangeIngestaIngresos,
  ingestaEgresos,
  onChangeIngestaEgresos,
  balanceHidrico,
  onChangeBalanceHidrico,
  gasometria,
  onChangeGasometria,
  ventilacion,
  onChangeVentilacion,
  procedimientos,
  onChangeProcedimientos,
  procedimientosEnfermeria,
  onChangeProcedimientosEnfermeria,
  procedimientosEnfermeriaHoras,
  onChangeProcedimientosEnfermeriaHoras,
  procedimientosOtros,
  onChangeProcedimientosOtros,
  solicitudExamenes,
  onChangeSolicitudExamenes,
  interconsultas,
  onChangeInterconsultas,
  informeEnfermeria,
  onChangeInformeEnfermeria,
}: Bloques114Props): JSX.Element {
  const { t } = useTranslation();
  const tablasBalance = tablasBalance114({ ingestaIngresos, ingestaEgresos, grupos });
  return (
    <div className="flex flex-col gap-6">
      <BloqueConstantesIngreso estado={constantesIngreso} onChange={onChangeConstantesIngreso} />
      <BloqueDatosBitacora estado={datosBitacora} onChange={onChangeDatosBitacora} />

      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.diagnosticoIngreso')}</h4>
        <SelectorDiagnosticoCie10 label={t('formularios.f114.diagnosticoIngreso')} value={diagnostico} onChange={onChangeDiagnostico} />
      </div>

      <BloqueGraficoUnaSerie titulo={t('formularios.f114.graficoTemperaturaTitulo')} puntos={graficoTemperatura} onChange={onChangeGraficoTemperatura} />
      <BloqueGraficoPaPulsoFr puntos={graficoPaPulsoFr} onChange={onChangeGraficoPaPulsoFr} />

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.medicacionTitulo')}</h4>
        <FilasNombrablesHorarias id="f114-medicacion" estado={grupos.medicacion} onChange={(v) => onChangeGrupos({ ...grupos, medicacion: v })} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f114.nebulizacionesTitulo')}</h5>
        <FilasNombrablesHorarias id="f114-nebulizaciones" estado={grupos.nebulizaciones} onChange={(v) => onChangeGrupos({ ...grupos, nebulizaciones: v })} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.ingestaIngresosTitulo')}</h4>
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f114.ingresosIntravenososTitulo')}</h5>
        <FilasNombrablesHorarias id="f114-ingresos-iv" unidad="mL" estado={grupos.ingresosIntravenosos} onChange={(v) => onChangeGrupos({ ...grupos, ingresosIntravenosos: v })} />
        <TablaHoraria24h filas={FILAS_INGESTA_INGRESOS_114} estado={ingestaIngresos} onChange={onChangeIngestaIngresos} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.ingestaEgresosTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_INGESTA_EGRESOS_114} estado={ingestaEgresos} onChange={onChangeIngestaEgresos} />
      </div>

      <BloqueBalanceHidrico estado={balanceHidrico} ingresos={tablasBalance.ingresos} egresos={tablasBalance.egresos} onChange={onChangeBalanceHidrico} />

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.gasometriaTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_GASOMETRIA_114} estado={gasometria} onChange={onChangeGasometria} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f114.otrosGasometriaTitulo')}</h5>
        <FilasNombrablesHorarias id="f114-otros-gasometria" estado={grupos.otrosGasometria} tope={TOPE_OTROS_HORARIOS_114} onChange={(v) => onChangeGrupos({ ...grupos, otrosGasometria: v })} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.ventilacionTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_VENTILACION_114} estado={ventilacion} onChange={onChangeVentilacion} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f114.otrosVentilacionTitulo')}</h5>
        <FilasNombrablesHorarias id="f114-otros-ventilacion" estado={grupos.otrosVentilacion} tope={TOPE_OTROS_HORARIOS_114} onChange={(v) => onChangeGrupos({ ...grupos, otrosVentilacion: v })} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.procedimientosTitulo')}</h4>
        <TablaConColumnas filas={FILAS_PROCEDIMIENTOS_114} columnas={COLUMNAS_PROCEDIMIENTOS_114} estado={procedimientos} onChange={onChangeProcedimientos} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f114.procedimientosOtrosTitulo')}</h5>
        <TablaConColumnas filas={FILAS_PROCEDIMIENTOS_OTROS_114} columnas={COLUMNAS_PROCEDIMIENTOS_OTROS_114} estado={procedimientosOtros} onChange={onChangeProcedimientosOtros} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.procedimientosEnfermeriaTitulo')}</h4>
        <ChecklistTurno items={ITEMS_PROCEDIMIENTOS_ENFERMERIA_114} estado={procedimientosEnfermeria} onChange={onChangeProcedimientosEnfermeria} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f114.procedimientosEnfermeriaHorasTitulo')}</h5>
        <TablaConColumnas filas={FILAS_PROCEDIMIENTOS_ENFERMERIA_114} columnas={COLUMNAS_PROCEDIMIENTOS_ENFERMERIA_HORAS_114} estado={procedimientosEnfermeriaHoras} onChange={onChangeProcedimientosEnfermeriaHoras} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.solicitudExamenesTitulo')}</h4>
        <TablaConColumnas filas={FILAS_SOLICITUD_EXAMENES_114} columnas={COLUMNAS_SOLICITUD_EXAMENES_114} estado={solicitudExamenes} onChange={onChangeSolicitudExamenes} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f114.interconsultasTitulo')}</h4>
        <TablaConColumnas filas={FILAS_INTERCONSULTAS_114} columnas={COLUMNAS_INTERCONSULTAS_114} estado={interconsultas} onChange={onChangeInterconsultas} />
      </div>

      <BloqueInformeEnfermeria114 estado={informeEnfermeria} onChange={onChangeInformeEnfermeria} />
    </div>
  );
}
